import { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Send, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const urlBase64ToUint8Array = (value) => {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
};

const standalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

/** Turns this device into a notification target. Must be triggered by a real tap (iOS rule). */
export const PushSetup = () => {
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [devices, setDevices] = useState([]);

  const supported = "serviceWorker" in navigator && "PushManager" in window;

  const check = useCallback(async () => {
    if (!supported) return setState("unsupported");
    try {
      const { data } = await api.get("/push/key");
      if (!data.enabled) return setState("unconfigured");
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      const { data: rows } = await api.get("/push/devices");
      setDevices(rows);
      setState(sub && rows.length ? "on" : Notification.permission === "denied" ? "blocked" : "off");
    } catch {
      setState("off");
    }
  }, [supported]);

  useEffect(() => {
    check();
  }, [check]);

  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/push/key");
      const reg = (await navigator.serviceWorker.getRegistration()) ||
        (await navigator.serviceWorker.register("/sw.js"));
      await navigator.serviceWorker.ready;
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState("blocked");
        toast.error("Notifications were not allowed on this device");
        setBusy(false);
        return;
      }
      const sub =
        (await reg.pushManager.getSubscription()) ||
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.public_key),
        }));
      const { data: saved } = await api.post("/push/subscribe", sub.toJSON());
      toast.success(`Notifications on for ${saved.device}`);
      await check();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not enable notifications");
    }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      } else {
        await api.post("/push/unsubscribe", {});
      }
      toast.success("Notifications turned off on this device");
      await check();
    } catch {
      toast.error("Could not turn notifications off");
    }
    setBusy(false);
  };

  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/push/test");
      if (data.sent) toast.success(`Test sent to ${data.sent} device${data.sent > 1 ? "s" : ""}`);
      else toast.error("No device received it — turn notifications on first");
    } catch {
      toast.error("Could not send the test");
    }
    setBusy(false);
  };

  if (state === "loading") return null;

  return (
    <div data-testid="push-setup" className="rounded-xl border border-border bg-card p-5">
      <h3 className="flex items-center gap-2 text-base font-display font-semibold text-royal-deep">
        <Bell className="h-4 w-4 text-royal" /> Push notifications
      </h3>
      <p className="mt-1.5 text-sm text-muted-foreground">
        Free alerts straight to this device — work orders, urgent issues, messages and weather. No
        text-message charges.
      </p>

      {state === "unsupported" && (
        <p data-testid="push-unsupported" className="mt-4 rounded-lg bg-muted p-3.5 text-sm text-muted-foreground">
          This browser cannot receive push notifications.
          {isIos() && " On iPhone, open Cornerstone Ops from the Safari share menu → Add to Home Screen, then open it from there."}
        </p>
      )}

      {state === "unconfigured" && (
        <p data-testid="push-unconfigured" className="mt-4 rounded-lg bg-muted p-3.5 text-sm text-muted-foreground">
          Push is not configured on this server yet.
        </p>
      )}

      {isIos() && !standalone() && state !== "unsupported" && (
        <p data-testid="push-ios-hint" className="mt-4 rounded-lg border border-gold/40 bg-gold/10 p-3.5 text-sm text-royal-deep">
          <Smartphone className="mr-1.5 inline h-4 w-4" />
          On iPhone you must open Cornerstone Ops from your home screen for notifications to work.
          Tap Share → Add to Home Screen, then open that icon and come back here.
        </p>
      )}

      {state === "blocked" && (
        <p data-testid="push-blocked" className="mt-4 rounded-lg bg-red-50 p-3.5 text-sm text-red-800">
          Notifications are blocked for this site. Allow them in your browser or iPhone settings,
          then try again.
        </p>
      )}

      {(state === "off" || state === "blocked") && (
        <Button
          data-testid="enable-push-btn"
          onClick={enable}
          disabled={busy}
          className="mt-4 w-full sm:w-auto bg-royal hover:bg-royal-deep text-white font-semibold"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Bell className="h-4 w-4 mr-2" /> Turn on for this device</>}
        </Button>
      )}

      {state === "on" && (
        <>
          <p data-testid="push-on" className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
            <Bell className="h-3.5 w-3.5" /> On for {devices.length} device{devices.length === 1 ? "" : "s"}
            {devices.length ? ` · ${devices.map((d) => d.label).join(", ")}` : ""}
          </p>
          <div className="mt-4 flex flex-wrap gap-2.5">
            <Button
              data-testid="test-push-btn"
              onClick={test}
              disabled={busy}
              variant="outline"
              className="border-royal/30 text-royal-deep hover:bg-royal/5 font-semibold"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send me a test</>}
            </Button>
            <Button
              data-testid="disable-push-btn"
              onClick={disable}
              disabled={busy}
              variant="ghost"
              className="text-muted-foreground hover:text-red-600"
            >
              <BellOff className="h-4 w-4 mr-2" /> Turn off
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default PushSetup;
